import { useApolloClient } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";
import Button from "@/components/Button";
import Checkbox from "@/components/Checkbox";
import Dialog from "@/components/Dialog";
import Radio from "@/components/Radio";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { copyTextToClipboard } from "@/lib/copyToClipboard";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import {
  buildCommitteeMailtoHref,
  fetchCommitteeParticipantEmails,
  formatEmailAddressList,
  isMailtoTooLong,
  openMailtoHref,
  type MailtoField
} from "./committeeMailto";

const EMPTY_EMAIL_RESULT = {
  emails: [] as string[],
  participantCount: 0,
  excludedCount: 0,
  truncated: false
};

export type ContactCommitteeMembersDialogProps = {
  open: boolean;
  committeeId: string | null;
  committeeName: string;
  inactiveParticipantCount: number;
  onClose(): void;
};

function emailCountCopy(count: number): string {
  return count === 1 ? "1 email address" : `${count} email addresses`;
}

function excludedCopy(count: number): string {
  return count === 1 ? "1 person has no usable email." : `${count} people have no usable email.`;
}

export default function ContactCommitteeMembersDialog({
  open,
  committeeId,
  committeeName,
  inactiveParticipantCount,
  onClose
}: ContactCommitteeMembersDialogProps) {
  const client = useApolloClient();
  const canViewPersonContact = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonContact
  });
  const [field, setField] = useState<MailtoField>("bcc");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);
  const [result, setResult] = useState(EMPTY_EMAIL_RESULT);

  useEffect(() => {
    if (!open) return;
    setField("bcc");
    setIncludeInactive(false);
    setResult(EMPTY_EMAIL_RESULT);
  }, [open]);

  useEffect(() => {
    if (!open || !committeeId || !canViewPersonContact) return;

    let cancelled = false;
    setLoading(true);

    void fetchCommitteeParticipantEmails(client, { committeeId, includeInactive })
      .then((nextResult) => {
        if (cancelled) return;
        setResult(nextResult);
        if (nextResult.truncated) {
          showErrorSnackbar("Not all email addresses loaded", {
            subtitle: "You can still copy or open email with the addresses that loaded."
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setResult(EMPTY_EMAIL_RESULT);
        showErrorSnackbar("Email addresses not loaded", { subtitle: "Please try again later." });
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, committeeId, includeInactive, canViewPersonContact, client]);

  const copyAddresses = useCallback(async () => {
    if (result.emails.length === 0) return false;

    try {
      await copyTextToClipboard(formatEmailAddressList(result.emails));
      return true;
    } catch {
      showErrorSnackbar("Email addresses not copied", { subtitle: "Please try again later." });
      return false;
    }
  }, [result.emails]);

  const handleCopyAddresses = useCallback(async () => {
    setCopying(true);
    try {
      const copied = await copyAddresses();
      if (copied) {
        showSnackbar("Email addresses copied");
      }
    } finally {
      setCopying(false);
    }
  }, [copyAddresses]);

  const handleOpenEmail = useCallback(async () => {
    if (result.emails.length === 0) return;

    const href = buildCommitteeMailtoHref({
      emails: result.emails,
      field,
      subject: committeeName
    });

    if (isMailtoTooLong(href)) {
      setCopying(true);
      try {
        const copied = await copyAddresses();
        if (copied) {
          showSnackbar("This list is too long to open in email. Addresses copied instead.");
        }
      } finally {
        setCopying(false);
      }
      return;
    }

    openMailtoHref(href);
  }, [committeeName, copyAddresses, field, result.emails]);

  const hasEmails = result.emails.length > 0;
  const actionsDisabled = loading || copying || !hasEmails;

  return (
    <Dialog
      open={open}
      headline="Email Members"
      hasFormElements
      onClosed={onClose}
      actions={
        <>
          <Button variant="text" onClick={onClose} testId="email-committee-members-cancel" disabled={copying}>
            Cancel
          </Button>
          <Button
            variant="text"
            onClick={() => void handleCopyAddresses()}
            disabled={actionsDisabled}
            testId="email-committee-members-copy"
          >
            Copy Addresses
          </Button>
          <Button
            variant="filled"
            onClick={() => void handleOpenEmail()}
            disabled={actionsDisabled}
            testId="email-committee-members-open"
          >
            Open Email
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6" data-testid="email-committee-members-dialog">
        <div className="flex flex-col gap-4">
          <Radio
            id="email-members-field-to"
            name="email-members-field"
            label="To"
            checked={field === "to"}
            onChange={(checked) => {
              if (checked) setField("to");
            }}
          />
          <Radio
            id="email-members-field-cc"
            name="email-members-field"
            label="Cc"
            checked={field === "cc"}
            onChange={(checked) => {
              if (checked) setField("cc");
            }}
          />
          <Radio
            id="email-members-field-bcc"
            name="email-members-field"
            label={<span id="email-members-field-bcc-label">Bcc</span>}
            aria-labelledby="email-members-field-bcc-label"
            supportingText="Hides addresses from other recipients."
            checked={field === "bcc"}
            onChange={(checked) => {
              if (checked) setField("bcc");
            }}
          />
        </div>
        <Checkbox
          name="include-inactive-members"
          label="Include Inactive Members"
          checked={includeInactive}
          disabled={inactiveParticipantCount === 0 || loading}
          onChange={setIncludeInactive}
        />
        <p className="text-body-medium">
          {loading
            ? "Loading email addresses…"
            : hasEmails
              ? `${emailCountCopy(result.emails.length)}.${result.excludedCount > 0 ? ` ${excludedCopy(result.excludedCount)}` : ""}`
              : result.excludedCount > 0
                ? `No email addresses. ${excludedCopy(result.excludedCount)}`
                : "No email addresses."}
        </p>
      </div>
    </Dialog>
  );
}
